import type { QuestionChoices, TableChoiceData } from '../types/questionChoices';

/**
 * PostgREST / drivers sometimes return jsonb as a string, or values may be double-encoded.
 * Keep parsing while the result is a JSON string so nested shapes (e.g. table MC) resolve.
 */
export function unwrapChoicesJson(raw: unknown): unknown {
  if (raw == null) return raw;
  let v: unknown = raw;
  const original = raw;
  for (let depth = 0; depth < 5; depth++) {
    if (typeof v !== 'string') return v;
    const s = v.trim();
    if (!s) return null;
    try {
      v = JSON.parse(s);
    } catch {
      return original;
    }
  }
  return v;
}

function parseTableShape(c: unknown): TableChoiceData | null {
  if (!c || typeof c !== 'object' || Array.isArray(c)) return null;
  const value = c as Record<string, unknown>;
  const t = value.type;
  const tableType =
    t === 'table' || (typeof t === 'string' && t.toLowerCase() === 'table');
  if (!tableType || !Array.isArray(value.columns) || !Array.isArray(value.rows)) return null;
  const rows = value.rows.filter(Array.isArray);
  return {
    type: 'table',
    columns: value.columns.map((column) => String(column ?? '')),
    rows: rows.map((row) => row.map((cell) => String(cell ?? ''))),
  };
}

/** Parsed table layout for rendering (handles jsonb returned as string / double-encoded JSON). */
export function getTableChoiceData(choices: unknown): TableChoiceData | null {
  return parseTableShape(unwrapChoicesJson(choices));
}

/** True if choices normalize to `{ type: 'table', columns, rows }`. */
export function isTableChoices(choices: unknown): boolean {
  return getTableChoiceData(choices) !== null;
}

export function isMultipleChoiceType(type: unknown): boolean {
  return type === 'mc' || (typeof type === 'string' && type.toLowerCase() === 'table');
}

export function isMultipleChoiceQuestion(type: unknown, choices: unknown): boolean {
  return isMultipleChoiceType(type) || isTableChoices(choices);
}

/** Flatten MC options to strings for scoring summaries / review lists (table rows → joined cells). */
export function toLegacyChoicesArray(choices: QuestionChoices | unknown): string[] {
  const c = unwrapChoicesJson(choices);
  if (Array.isArray(c)) return c.map((x) => String(x ?? ''));
  const table = parseTableShape(c);
  if (table) {
    return table.rows.map((row) => row.map((cell) => String(cell ?? '')).join(' | '));
  }
  return [];
}

export function getChoiceCount(choices: unknown): number {
  const c = unwrapChoicesJson(choices);
  if (Array.isArray(c)) return c.length;
  const table = parseTableShape(c);
  return table ? table.rows.length : 0;
}

/** Normalize DB `choices` JSON for code paths that still expect `string[]` (review lines, scoring text). */
export function choicesAsFlatStrings(choices: unknown): string[] {
  const c = unwrapChoicesJson(choices);
  if (c == null) return [];
  if (Array.isArray(c)) return c.filter((x): x is string => typeof x === 'string');
  if (parseTableShape(c)) return toLegacyChoicesArray(c);
  if (typeof c === 'object') {
    return Object.values(c as Record<string, unknown>).filter((v): v is string => typeof v === 'string');
  }
  return [];
}
